import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useMemo } from 'react';

// ContractPacingTable (ADOPS-2694) is the pivot-table alternative to the "Pacing
// by Line Items" line chart: rows are days, and each selected line item gets a
// pair of columns (Impressions vs. Goal). It renders from the same filtered data
// the chart consumes so the two views always agree.

export interface PacingPoint {
  x: string; // report day, YYYY-MM-DD
  y1: number | string | null; // goal impressions
  y2: number | string | null; // delivered impressions
}

interface ContractPacingTableProps {
  // Line item ids to show, in the same order/selection the chart uses.
  lineItemIds: string[];
  // Raw per-line-item pacing points, keyed by line item id.
  dataByLineItemId: Record<string, PacingPoint[]>;
}

// Match the line chart's values: it scales each figure by 100 (see
// ContractPacingData), so the table shows the same scaled number — just without
// the chart's `%` suffix. Null/absent values render as an em dash.
function formatMetric(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return (n * 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// Parse a YYYY-MM-DD day as a LOCAL date (splitting the parts) so display doesn't
// drift a day under UTC parsing.
function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ContractPacingTable({ lineItemIds, dataByLineItemId }: ContractPacingTableProps) {
  // Collect the sorted set of days across the selected line items and a
  // day → lineItemId → point lookup, so each cell is an O(1) read.
  const { days, lookup } = useMemo(() => {
    const daySet = new Set<string>();
    const byDay: Record<string, Record<string, PacingPoint>> = {};
    lineItemIds.forEach((id) => {
      (dataByLineItemId[id] || []).forEach((point) => {
        daySet.add(point.x);
        if (!byDay[point.x]) byDay[point.x] = {};
        byDay[point.x][id] = point;
      });
    });
    const sortedDays = Array.from(daySet).sort((a, b) => a.localeCompare(b));
    return { days: sortedDays, lookup: byDay };
  }, [lineItemIds, dataByLineItemId]);

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Table size="small" stickyHeader data-testid="pacing-table">
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2} sx={{ fontWeight: 700, verticalAlign: 'bottom' }}>
              Date
            </TableCell>
            {lineItemIds.map((id) => (
              <TableCell key={id} colSpan={2} align="center" sx={{ fontWeight: 700 }}>
                Insertion Order {id}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            {lineItemIds.map((id) => (
              <React.Fragment key={id}>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Impressions
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Goal
                </TableCell>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map((day) => (
            <TableRow key={day} hover data-testid="pacing-row">
              <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                {formatDay(day)}
              </TableCell>
              {lineItemIds.map((id) => {
                const point = lookup[day]?.[id];
                return (
                  <React.Fragment key={id}>
                    <TableCell align="right">{formatMetric(point?.y2)}</TableCell>
                    <TableCell align="right">{formatMetric(point?.y1)}</TableCell>
                  </React.Fragment>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ContractPacingTable;
